'use client'

import { RECOMMEND_VISITOR_ID_COOKIE_KEY } from '@/constants/recommend'
import { useBISdkContext } from '@/context/BISdkContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { getClientCookie } from '@/lib/client-cookie'
import { fetchMoreVideoItems } from '@/services/world-recommendations'
import type { MoreVideoItem } from '@/types/more-videos'
import {
  Suspense,
  createContext,
  use,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

type PanelReason = 'ended' | 'manual'
type PendingOpen = { reason: PanelReason; anchorBottom: number | null }

type MoreVideosPanelContextType = {
  isOpen: boolean
  isLoading: boolean
  reason: PanelReason | null
  anchorBottom: number | null
  items: MoreVideoItem[]
  openPanel: (reason: PanelReason, anchorBottom?: number | null) => void
  closePanel: () => void
}

const MoreVideosPanelContext = createContext<MoreVideosPanelContextType | null>(null)

function ItemsSuspender({
  promise,
  onLoad,
}: {
  promise: Promise<MoreVideoItem[]>
  onLoad: (items: MoreVideoItem[]) => void
}) {
  const items = use(promise)
  useEffect(() => {
    onLoad(items)
  }, [items, onLoad])
  return null
}

export function MoreVideosPanelProvider({
  children,
  moreVideoItemsPromise,
  worldId,
}: {
  children: ReactNode
  moreVideoItemsPromise: Promise<MoreVideoItem[]> | null
  worldId: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [reason, setReason] = useState<PanelReason | null>(null)
  const [anchorBottom, setAnchorBottom] = useState<number | null>(null)
  const [items, setItems] = useState<MoreVideoItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [clientItemsPromise, setClientItemsPromise] = useState<Promise<MoreVideoItem[]> | null>(
    null,
  )

  const [pendingOpen, setPendingOpen] = useState<PendingOpen | null>(null)
  const { isLoadedBISdk, isBISdkFailed } = useBISdkContext()
  const { appConfig } = useConfigsContext()
  const shouldWaitForBISdk = Boolean(appConfig?.features?.bi_sdk_uri)
  const itemsPromise = moreVideoItemsPromise ?? clientItemsPromise

  useEffect(() => {
    if (moreVideoItemsPromise !== null || clientItemsPromise !== null) return
    if (shouldWaitForBISdk && !isLoadedBISdk && !isBISdkFailed) return

    setClientItemsPromise(
      fetchMoreVideoItems({
        worldId,
        csdataBid: getClientCookie(RECOMMEND_VISITOR_ID_COOKIE_KEY),
      })
        .then((result) => (result.ok ? result.items : []))
        .catch(() => []),
    )
  }, [
    clientItemsPromise,
    isBISdkFailed,
    isLoadedBISdk,
    moreVideoItemsPromise,
    shouldWaitForBISdk,
    worldId,
  ])

  const handleItemsLoad = useCallback((loadedItems: MoreVideoItem[]) => {
    setItems(loadedItems)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (isLoading || pendingOpen === null) return

    setPendingOpen(null)
    if (items.length === 0) return

    setAnchorBottom(pendingOpen.anchorBottom)
    setReason(pendingOpen.reason)
    setIsOpen(true)
  }, [isLoading, items.length, pendingOpen])

  const openPanel = useCallback(
    (openReason: PanelReason, anchor: number | null = null) => {
      if (isLoading) {
        setPendingOpen({ reason: openReason, anchorBottom: anchor })
        return
      }
      if (items.length === 0) return
      setAnchorBottom(anchor)
      if (!isOpen) {
        setReason(openReason)
        setIsOpen(true)
      }
    },
    [isLoading, isOpen, items.length],
  )

  const closePanel = useCallback(() => {
    setIsOpen(false)
    setReason(null)
    setAnchorBottom(null)
    setPendingOpen(null)
  }, [])

  return (
    <MoreVideosPanelContext
      value={{ isOpen, isLoading, reason, anchorBottom, items, openPanel, closePanel }}
    >
      {children}

      {itemsPromise && (
        <Suspense fallback={null}>
          <ItemsSuspender promise={itemsPromise} onLoad={handleItemsLoad} />
        </Suspense>
      )}
    </MoreVideosPanelContext>
  )
}

export function useMoreVideosPanelContext() {
  const ctx = use(MoreVideosPanelContext)
  if (!ctx) throw new Error('useMoreVideosPanelContext must be used within MoreVideosPanelProvider')
  return ctx
}
